'use client';

import Image from 'next/image';
import LinkNoPrefetch from '@/components/link-noprefetch';
import { usePathname } from 'next/navigation';
import { IconCheck, IconCircleCheck } from '@tabler/icons-react';
import { CallToActionButton } from '@/components/shared/CallToActionButton';
import Badge from '../shared/badge';
import { cn } from '@/app/utils/cn';
import { DotBlockEditor } from '../shared/decodeTextBlock';
import { decode } from 'html-entities';
import { DotCMSBlockEditorRenderer } from '@dotcms/react';
import { isBlockEditorEmpty, splitAfterBody } from '@/app/utils';
import { DotCardIcon } from './dotCardIcon';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';

function Heading({ node: { attrs, content } = {} }) {
    if (!content) return null;

    const [{ text }] = content;
    const HeadingTag = `h${attrs?.level || 3}`;

    return (
        <HeadingTag
            className="dark:text-white"
            style={{
                textAlign: attrs?.textAlign || 'left'
            }}>
            {decode(text)}
        </HeadingTag>
    );
}

function ParagraphNode({ node }) {
    if (node.type === 'hardBreak') return <br />;

    const { marks = [], text } = node;

    if (text === undefined) return null;

    return marks.reduceRight((content, mark) => {
        if (mark.type === 'link') {
            return (
                <LinkInParagraph url={mark.attrs?.href} attributes={mark.attrs}>
                    {content}
                </LinkInParagraph>
            );
        }
        if (mark.type === 'bold') return <strong>{content}</strong>;
        if (mark.type === 'italic') return <em>{content}</em>;
        if (mark.type === 'underline') return <u>{content}</u>;
        return content;
    }, decode(text));
}

function Paragraph({ node: { content } }) {
    if (!content) return null;

    return (
        <p className="dark:text-slate-300">
            {content.map((node, index) => (
                <ParagraphNode key={index} node={node} />
            ))}
        </p>
    );
}

function LinkInParagraph({ url, attributes, children }) {
    return (
        <LinkNoPrefetch
            href={url}
            target={attributes?.target || '_self'}
            rel={attributes?.rel || ''}
            className={attributes?.class || 'text-section-color hover:underline'}>
            {children}
        </LinkNoPrefetch>
    );
}

function ListItem({ node: { content } }) {
    if (!content) return null;

    const [item] = content;
    const { content: itemContent } = item;

    return (
        <li>
            {itemContent.map((textItem, index) => {
                const { text, marks } = textItem;
                let element = decode(text);

                if (marks && marks.length > 0) {
                    [...marks].reverse().forEach((mark) => {
                        switch (mark.type) {
                            case 'bold':
                                element = <strong>{element}</strong>;
                                break;
                            case 'italic':
                                element = <em>{element}</em>;
                                break;
                            case 'underline':
                                element = <u>{element}</u>;
                                break;
                            default:
                                element = <span>{element}</span>;
                        }
                    });
                }

                return <span key={`text-${index}`}>{element}</span>;
            })}
        </li>
    );
}

function GlobalGridWrapper({ children }) {
    return (
        <div className="grid items-center justify-start gap-6 sm:grid-cols-[repeat(auto-fit,minmax(17.5rem,1fr))] md:gap-12 lg:gap-20 xl:gap-24">
            {children}
        </div>
    );
}

function GlobalCustomHeading({ children, sectionColor }) {
    const twClasses = cn(
        'dot-card-header text-2xl mt-0 dark:text-white',
        sectionColor ? 'text-section-color' : ''
    );

    return <h3 className={twClasses}>{children}</h3>;
}

function Layout01({ props }) {
    const { label, image, body, headline, callToAction, identifier, headingSectionColor, _map } =
        props;

    const [button] = callToAction || [];

    return (
        <>
            {label && (
                <h2 className="mb-2 text-base">
                    <Badge>{label}</Badge>
                </h2>
            )}
            <GlobalGridWrapper>
                <div className="flex flex-1 flex-col justify-between">
                    <div className="prose max-w-none">
                        {headline && (
                            <GlobalCustomHeading sectionColor={headingSectionColor}>
                                {headline}
                            </GlobalCustomHeading>
                        )}
                        {body && (
                            <DotBlockEditor
                                blocks={body}
                                customRenderers={{
                                    listItem: ListItem,
                                    paragraph: Paragraph,
                                    heading: Heading
                                }}
                            />
                        )}
                    </div>

                    {button && button.urlName && button.url && (
                        <div>
                            <CallToActionButton
                                {...button}
                                ariaLabel={`Go to ${button.urlName || button.url}`}
                                urlName="Learn More"
                                className={cn(button.className, 'no-underline')}
                            />
                        </div>
                    )}
                </div>
                {image && (
                    <Image
                        src={`/dA/${identifier}/image`}
                        alt="image"
                        width={0}
                        height={0}
                        quality={55}
                        sizes="(max-width: 1200px) calc(100vw - 32px), 1200px"
                        className="aspect-auto w-full object-contain"
                        decoding="async"
                    />
                )}
                {_map && _map.image && (
                    <Image
                        src={getDotcmsImageURL({ src: _map.image.identifier })}
                        alt="image"
                        width={0}
                        height={0}
                        quality={55}
                        sizes="(max-width: 600px) calc(100vw - 32px), 600px"
                        className="aspect-auto w-full object-contain"
                    />
                )}
            </GlobalGridWrapper>
        </>
    );
}

function Layout02({ props }) {
    const { image, body, headline, identifier, headingSectionColor, callToAction } = props;
    const [button] = callToAction || [];

    return (
        <>
            <GlobalGridWrapper>
                <>
                    {image && (
                        <Image
                            src={`/dA/${identifier}/image`}
                            alt="image"
                            width={0}
                            height={0}
                            quality={55}
                            sizes="(max-width: 600px) calc(100vw - 32px), 600px"
                            className="order-last aspect-auto w-full object-contain sm:order-none"
                        />
                    )}
                    <div className="prose max-w-none">
                        {headline && (
                            <GlobalCustomHeading sectionColor={headingSectionColor}>
                                {headline}
                            </GlobalCustomHeading>
                        )}
                        {body && (
                            <DotBlockEditor
                                blocks={body}
                                customRenderers={{
                                    listItem: ListItem,
                                    heading: Heading,
                                    paragraph: Paragraph
                                }}
                            />
                        )}
                        {button && button.urlName && button.url && (
                            <CallToActionButton {...button}>{button.urlName}</CallToActionButton>
                        )}
                    </div>
                </>
            </GlobalGridWrapper>
        </>
    );
}

function Layout03({ props }) {
    const {
        image,
        body,
        headline,
        identifier,
        headingSectionColor,
        video,
        afterBody,
        callToAction
    } = props;
    const [button] = callToAction || [];

    const [leftBlocks, rightBlocks] = splitAfterBody(afterBody?.content);

    function ListItem({ content }) {
        if (!content) {
            return null;
        }
        const [item] = content;
        const { content: itemContent } = item;
        const [{ text }] = itemContent;
        return (
            <li className="m-0 flex items-center py-1 dark:text-white">
                <IconCircleCheck size={24} className="mr-2 text-orange-650" />
                {decode(text)}
            </li>
        );
    }

    return (
        <>
            <GlobalGridWrapper>
                <div className="prose max-w-none">
                    {headline && (
                        <GlobalCustomHeading sectionColor={headingSectionColor}>
                            {headline}
                        </GlobalCustomHeading>
                    )}
                    {body && (
                        <DotBlockEditor blocks={body} customRenderers={{ listItem: ListItem }} />
                    )}
                    {button && button.urlName && button.url && (
                        <div>
                            <CallToActionButton {...button}>{button.urlName}</CallToActionButton>
                        </div>
                    )}
                </div>
                {video && Object.keys(video).length > 0 ? (
                    <video
                        className="h-auto max-h-[80%] w-full rounded-lg border border-indigo-500 object-cover p-2"
                        controls>
                        <source src={`/dA/${video}`}></source>
                        Your browser does not support the video tag.
                    </video>
                ) : image ? (
                    <Image
                        src={`/dA/${identifier}/image`}
                        alt="image"
                        width={0}
                        height={0}
                        quality={55}
                        sizes="(max-width: 600px) calc(100vw - 32px), 600px"
                        className="aspect-auto w-full object-contain"
                    />
                ) : null}
            </GlobalGridWrapper>

            {!isBlockEditorEmpty(afterBody) && (
                <div className="mt-8 rounded-lg bg-orange-50 p-8">
                    <GlobalGridWrapper>
                        {!isBlockEditorEmpty(leftBlocks) && (
                            <DotCMSBlockEditorRenderer
                                blocks={leftBlocks}
                                className="text-blue-700"
                            />
                        )}
                        {!isBlockEditorEmpty(rightBlocks) && (
                            <DotCMSBlockEditorRenderer
                                blocks={rightBlocks}
                                customRenderers={{ listItem: ListItem }}
                            />
                        )}
                    </GlobalGridWrapper>
                </div>
            )}
        </>
    );
}

function Layout04({ props }) {
    const { image, body, headline, icon, identifier, headingSectionColor, callToAction, _map } =
        props;
    const [button] = callToAction || [];
    return (
        <GlobalGridWrapper>
            <div className="prose max-w-none">
                {icon && <DotCardIcon icon={icon} alt={headline} />}
                {headline && (
                    <GlobalCustomHeading sectionColor={headingSectionColor}>
                        {headline}
                    </GlobalCustomHeading>
                )}
                {body && <DotBlockEditor blocks={body} customRenderers={{ listItem: ListItem }} />}
                {button && button.urlName && button.url && (
                    <CallToActionButton {...button}>{button.urlName}</CallToActionButton>
                )}
            </div>
            {image && (
                <Image
                    src={`/dA/${identifier}/image`}
                    alt="image"
                    width={0}
                    height={0}
                    quality={55}
                    sizes="(max-width: 600px) calc(100vw - 32px), 600px"
                    className="aspect-auto w-full object-contain"
                />
            )}
            {_map && _map.image && (
                <Image
                    src={getDotcmsImageURL({ src: _map.image.identifier })}
                    alt="image"
                    width={0}
                    height={0}
                    quality={55}
                    sizes="(max-width: 600px) calc(100vw - 32px), 600px"
                    className="aspect-auto w-full object-contain"
                />
            )}
        </GlobalGridWrapper>
    );
}

function Layout05({ props }) {
    function Heading({ node: { content } }) {
        if (!content) {
            return null;
        }
        const [{ text }] = content;
        return (
            <h3 className="mb-4 border-b py-4 text-4xl text-fuchsia-500 dark:border-blue-500 dark:text-green-700">
                {decode(text)}
            </h3>
        );
    }

    function Paragraph({ node: { content } }) {
        if (!content) {
            return null;
        }
        const [{ text }] = content;
        return (
            <p className="m-0 mb-1 border-b pb-4 dark:border-blue-500 dark:text-white">
                {decode(text)}
            </p>
        );
    }

    function ListItem({ node: { content } }) {
        if (!content) {
            return null;
        }
        const [item] = content;
        const { content: itemContent } = item;
        const [{ text }] = itemContent;
        return (
            <li className="m-0 flex items-center gap-1 py-1 dark:text-white">
                <IconCheck size={16} className="min-w-5 text-green-700" />
                {decode(text)}
            </li>
        );
    }

    const { body, headline, icon, callToAction } = props;
    const [button] = callToAction || [];

    return (
        <div
            data-layout="5"
            className="flex h-full flex-1 flex-col overflow-visible rounded-xl border-2 border-slate-200 p-8 dark:border-0 dark:bg-blue-700">
            <div className="prose mb-4 prose-h2:mt-0 prose-img:m-0">
                {icon && (
                    <Image
                        src={icon?.idPath ?? icon}
                        alt="icon"
                        width={64}
                        height={64}
                        className="mb-2 object-contain"
                    />
                )}
                {headline && <h2 className="text-2xl dark:text-green-700">{headline}</h2>}
            </div>
            {body && (
                <div className="mb-6 grow">
                    <DotBlockEditor
                        blocks={body}
                        customRenderers={{
                            heading: Heading,
                            paragraph: Paragraph,
                            listItem: ListItem
                        }}
                    />
                </div>
            )}
            <hr className="my-4 border-t border-gray-300/60" />
            {button && button.urlName && button.url && (
                <CallToActionButton {...button} className="w-full">
                    {button.urlName}
                </CallToActionButton>
            )}
        </div>
    );
}

function Layout06({ props }) {
    function Heading(props) {
        const {
            node: { content, text }
        } = props;
        const headingText = text || (content && content[0]?.text);
        if (!headingText) {
            return null;
        }
        return (
            <h3 className="font-inter py-4 text-[22px] font-bold leading-[118%] tracking-normal !text-black md:text-[30px] md:leading-[110%] lg:text-[38px] lg:leading-[110%] dark:border-blue-500 dark:text-green-700">
                {decode(headingText)}
            </h3>
        );
    }

    function Paragraph({ content }) {
        if (!content) {
            return null;
        }
        const [{ text }] = content;
        return <p className="m-0 mb-1 pb-4  dark:text-white">{decode(text)}</p>;
    }

    function ListItem({ content }) {
        if (!content) {
            return null;
        }
        const [item] = content;
        const { content: itemContent } = item;
        const [{ text }] = itemContent;
        return (
            <li className="m-0 flex items-center py-1 dark:text-white">
                <IconCheck size={16} className="mr-2 text-green-700" />
                {decode(text)}
            </li>
        );
    }

    const { headline, image, body, callToAction, identifier } = props;
    const [button] = callToAction || [];

    return (
        <div className="background-dotcms mx-auto flex min-h-[430px] w-full max-w-[1215px] items-center rounded-[26px] !bg-repeat px-4 py-8 md:!bg-cover md:!bg-no-repeat md:py-8 dark:border-0 dark:bg-blue-700">
            <div className="flex w-full flex-col items-center gap-12 md:flex-row md:items-center md:justify-between md:px-[80px]">
                <div className="flex w-full flex-1 flex-col justify-start md:w-auto">
                    <div>
                        {headline && (
                            <Badge className="self-start text-[16px] uppercase">{headline}</Badge>
                        )}
                    </div>
                    {body && (
                        <DotBlockEditor
                            className="mb-3"
                            blocks={body}
                            customRenderers={{
                                heading: Heading,
                                paragraph: Paragraph,
                                listItem: ListItem
                            }}
                        />
                    )}
                    {button && button.urlName && button.url && (
                        <div className="flex w-full justify-center md:block md:w-auto">
                            <CallToActionButton {...button} className="w-full md:w-fit">
                                {button.urlName}
                            </CallToActionButton>
                        </div>
                    )}
                </div>
                <div className="flex items-center justify-center">
                    {image && (
                        <Image
                            src={`/dA/${identifier}/image`}
                            alt="image"
                            width={261}
                            height={258}
                            quality={55}
                            sizes="261px"
                            className="size-[150px] object-contain md:h-[258px] md:w-[261px] lg:h-[338px] lg:w-[331px]"
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function DotCard(props) {
    const pathName = usePathname();
    const layoutComponents = {
        1: Layout01,
        2: Layout02,
        3: Layout03,
        4: Layout04,
        5: Layout05,
        6: Layout06
    };
    const LayoutComponent = layoutComponents[props.layout] || Layout01;
    const categoryClassName =
        (props?.category?.[0] &&
            typeof props.category[0] === 'object' &&
            `${Object.values(props.category[0])?.[0]}`) ??
        null;

    return (
        <div
            data-component="feature-section"
            data-layout={props.layout}
            className={`container sm:px-0 ${categoryClassName ?? ''} ${props.layout === 5 ? 'h-full' : ''}`}>
            <LayoutComponent props={props} pathName={pathName} />
        </div>
    );
}

export default DotCard;
